import React, { useEffect, useLayoutEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import "./styles/performance-optimizations.css";
import "./styles/syntax-highlighting.css";
import LandingPage from "./components/LandingPage";
import OptimizedLandingPage from "./components/OptimizedLandingPage";
import ForCompaniesPage from "./components/ForCompaniesPage";
import ContactPage from "./components/ContactPage";
import SoftwareProtectionPage from "./components/SoftwareProtectionPage";
import CaseStudiesIndex from "./components/caseStudies/CaseStudiesIndex";
import CaseStudyDetail from "./components/caseStudies/CaseStudyDetail";
import { getCaseStudyPath, getPreferredCaseStudyLocale } from "./data/caseStudies";

// Blog Components
import BlogList from "./components/blog/BlogList";
import BlogPost from "./components/blog/BlogPost";
import AdminLogin from "./components/blog/AdminLogin";
import AdminDashboard from "./components/blog/AdminDashboard";
import BlogEditor from "./components/blog/BlogEditor";
import AuthWrapper from "./components/blog/AuthWrapper";

// Global Components
import TitleManager from "./components/TitleManager";

// Transition Components
import RouteTransition from "./components/transitions/RouteTransition";
import NavigationLoader from "./components/loading/NavigationLoader";

// Contexts
import { AuthProvider } from "./contexts/AuthContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { TitleProvider } from "./contexts/TitleContext";
import { scrollToTopInstant } from "./utils/scrollUtils";

// Browser detection
const isChromium = () => {
  return /Chrome|Chromium|Edge/.test(navigator.userAgent) && !/Firefox/.test(navigator.userAgent);
};

const RouteScrollManager = () => {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return;

    scrollToTopInstant();
  }, [pathname, hash]);

  return null;
};

function App() {
  const [useOptimizedVersion, setUseOptimizedVersion] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [animationType, setAnimationType] = useState('blur');

  useEffect(() => {
    // Automatically use optimized version for Chromium browsers
    const chromiumBrowser = isChromium();
    setUseOptimizedVersion(chromiumBrowser);

    if (chromiumBrowser) {
      // Apply basic Chromium optimizations
      document.documentElement.style.transform = 'translate3d(0, 0, 0)';
      document.documentElement.style.scrollBehavior = 'smooth';
    }
  }, []);

  // Landing Page Component with shared menu state
  const LandingPageComponent = () => {
    return useOptimizedVersion ? (
      <OptimizedLandingPage isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
    ) : (
      <LandingPage isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
    );
  };

  // BlogPost wrapper to handle proper re-rendering
  const BlogPostWrapper = () => {
    const { slug } = useParams();
    return <BlogPost key={slug} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />;
  };

  const CaseStudyDetailWrapper = () => {
    const { slug } = useParams();
    return (
      <CaseStudyDetail
        key={slug}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />
    );
  };

  const CaseStudyPreferredRedirect = () => {
    const { slug } = useParams();
    const locale = getPreferredCaseStudyLocale();
    return <Navigate to={getCaseStudyPath(locale, slug)} replace />;
  };
return (
 <HelmetProvider>
   <TitleProvider>
     <LoadingProvider>
       <AuthProvider>
         <Router>
           <div className="App">
             <RouteScrollManager />

             {/* Global Title and SEO Management */}
             <TitleManager />

             <NavigationLoader />
             <AuthWrapper>
               {/*<RouteTransition animationType={animationType}> */}
                 <Routes>
                   {/* Landing Page */}
                   <Route path="/" element={<LandingPageComponent />} />

                   {/* For Companies */}
                   <Route
                     path="/for-companies"
                     element={<ForCompaniesPage isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />

                   {/* Software Protection */}
                   <Route
                     path="/software-protection"
                     element={<SoftwareProtectionPage isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />

                   {/* Contact */}
                   <Route
                     path="/contact"
                     element={<ContactPage isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />

                   {/* Case Study Routes */}
                   <Route path="/case-studies" element={<CaseStudyPreferredRedirect />} />
                   <Route path="/case-studies/:slug" element={<CaseStudyPreferredRedirect />} />
                   <Route
                     path="/:locale/case-studies"
                     element={<CaseStudiesIndex isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />
                   <Route
                     path="/:locale/case-studies/:slug"
                     element={<CaseStudyDetailWrapper />}
                   />

                   {/* Blog Routes */}
                   <Route
                     path="/blog"
                     element={<BlogList isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />
                   <Route
                     path="/blog/:slug"
                     element={<BlogPostWrapper />}
                   />

                   {/* Admin Routes */}
                   <Route path="/blog/admin" element={<AdminLogin />} />
                   <Route path="/blog/admin/login" element={<AdminLogin />} />
                   <Route
                     path="/blog/admin/dashboard"
                     element={<AdminDashboard isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />
                   <Route
                     path="/blog/admin/editor"
                     element={<BlogEditor isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />
                   <Route
                     path="/blog/admin/editor/:postId"
                     element={<BlogEditor isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />}
                   />
                 </Routes>
               {/*</RouteTransition> */}
             </AuthWrapper>
           </div>
         </Router>
       </AuthProvider>
     </LoadingProvider>
   </TitleProvider>
 </HelmetProvider>
);
}

export default App;
